import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Alert, Autocomplete, TextField, Tooltip } from "@mui/material";
import { ModalConfirmDialog } from "components";
import DisplayUnitsDataGrid from "./DisplayUnitsDataGrid";
import {
  DisplayUnitsDataGridRow,
  RoundMethod,
  UnitInfo,
  UnitPickerDialogProps,
} from "./UnitPickerInterfaces";
import {
  BuildSubproperty,
  CreateDisplayUnitsDataGridRow,
  GetAllUnits,
  GetGroupUnits,
  GetPropertyValues,
  GetUnitGroupFromUnit,
  ValidateRoundDigits,
  ValidateRoundMethod,
} from "./UnitPickerUtility";
import { useTypedSelector } from "store";
import {
  ParentPropertyContextType,
  useParentPropertyContext,
} from "../../pages/ProductDetail/ProductLineProperties/ParentPropertyContext";
import { StandardSubproperties } from "store/reducers/products/subproperties/pl-subproperties.types";
import { StandardPVAs } from "store/reducers/products/parameters/pl-parameters.types";
import {
  PropertyResponse,
  Property,
} from "store/reducers/products/properties/pl-properties.types";
import {
  createOrReplaceStandardSubprop,
  removeStandardSubPropertyPL,
} from "store/reducers/products/subproperties/pl-subproperties.actions";

export const UnitPickerDialog: React.FC<UnitPickerDialogProps> = ({
  open,
  setOpen,
}) => {
  // State
  const [primaryUnit, setPrimaryUnit] = useState<UnitInfo | null>(null);
  const [activeGroupUnits, setActiveGroupUnits] = useState<string[]>([]);
  const [rows, setRows] = useState<DisplayUnitsDataGridRow[]>([]);
  const [showWarning] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [isGridInEditMode, setIsGridInEditMode] = useState<boolean>(false);

  // References
  const allUnitsRef = useRef<UnitInfo[]>([]);
  useEffect(() => {
    allUnitsRef.current = GetAllUnits();
  }, []);

  // Redux Store
  const dispatch = useDispatch();
  const { propertyValueAttributes } = useTypedSelector(
    (state) => state.products.parameters
  );
  const { subproperties } = useTypedSelector((state) => state.products);

  // context variables
  const parentPropertyContext: ParentPropertyContextType =
    useParentPropertyContext();

  useEffect(() => {
    //Load the component state from the parent propery and subproperties
    loadStateFromProperties();
  }, []);

  useEffect(() => {
    if (!loading) {
      //Get the units for the new primary unit's group
      const groupUnits: string[] = GetGroupUnits(
        primaryUnit?.group,
        allUnitsRef.current
      );

      //Sync the rows so they only contain display units in the primary unit's group
      //and also contains the primary unit.
      const newRows = syncRowsToPrimaryUnit(
        primaryUnit?.label,
        groupUnits,
        rows
      );

      //Save the group units and new rows to state
      setActiveGroupUnits(groupUnits);
      setRows(newRows);
    }
  }, [primaryUnit]);

  const syncRowsToPrimaryUnit = (
    primaryUnitValue: string | undefined,
    groupUnits: string[],
    displayUnitRows: DisplayUnitsDataGridRow[]
  ): DisplayUnitsDataGridRow[] => {
    //Remove any display unit rows that aren't in the primary unit's group
    const newRows: DisplayUnitsDataGridRow[] = displayUnitRows.filter((r) =>
      groupUnits.includes(r.displayUnit)
    );

    if (primaryUnitValue) {
      //Determine if existing rows already have a default display unit
      const hasDefaults = newRows.filter((r) => r.isDefault).length > 0;

      //Find the row for the primary unit, if it exists
      const primaryUnitRow = newRows.find(
        (r) => r.displayUnit === primaryUnitValue
      );

      //If we don't have a row for the primary unit, add it to the beginning of
      //display unit rows and make it the default unit
      // if we don't already have a default.
      if (!primaryUnitRow) {
        newRows.unshift(
          CreateDisplayUnitsDataGridRow(primaryUnitValue, !hasDefaults)
        );
      }
    }

    return newRows;
  };

  const loadStateFromProperties = () => {
    //Get the Primary Unit from .UNIT subproperty
    const primaryUnitValue: string = getPrimaryUnit();
    if (primaryUnitValue) {
      const unit: UnitInfo = allUnitsRef.current.filter(
        (u) => u.label === primaryUnitValue
      )[0];
      if (unit) setPrimaryUnit(unit);
    }

    //Get the unit group from the primaryUnitValue and the units for that group
    const unitGroup: string = GetUnitGroupFromUnit(primaryUnitValue);
    const groupUnits: string[] = GetGroupUnits(unitGroup, allUnitsRef.current);
    setActiveGroupUnits(groupUnits);

    //Build the Display Units
    let displayUnitRows: DisplayUnitsDataGridRow[] = getDisplayUnits();

    //Load the Default Unit into the rows
    const defaultUnit: string = getDefaultUnit();
    const defaultRow: DisplayUnitsDataGridRow | null | undefined = defaultUnit
      ? displayUnitRows.find((row) => row.displayUnit === defaultUnit)
      : null;
    if (defaultRow) defaultRow.isDefault = true;

    //Sync the primary unit to the rows
    displayUnitRows = syncRowsToPrimaryUnit(
      primaryUnitValue,
      groupUnits,
      displayUnitRows
    );

    //Set the rows prop
    if (displayUnitRows) setRows(displayUnitRows);

    //Flag loading is complete
    setTimeout(() => {
      setLoading(false);
    }, 300);
  };

  const getPrimaryUnit = (): string => {
    let unitValue = "";

    //See if the .UNIT subproperty exists for the current parent property
    const unitSubProp = findSubProperty(StandardSubproperties.UNIT);
    if (unitSubProp) {
      //Read the domain values of the subproperty and get the first one for the .UNIT
      const values: string[] | undefined = GetPropertyValues(
        unitSubProp
      )?.filter((v) => {
        if (v) return v;
      });
      unitValue = values && values.length > 0 ? values[0] : "";

      //Verify that the unit value is a legitimate unit
      if (!allUnitsRef.current.find((ui) => ui.label === unitValue))
        unitValue = "";
    }

    return unitValue;
  };

  const getDisplayUnits = (): DisplayUnitsDataGridRow[] => {
    const displayUnitRows: DisplayUnitsDataGridRow[] = [];

    //See if the .DISPLAYUNIT subproperty exists for the current parent property
    const displayUnitSubProp = findSubProperty(
      StandardSubproperties.DISPLAYUNIT
    );
    if (
      displayUnitSubProp &&
      displayUnitSubProp.propertyValues &&
      displayUnitSubProp.propertyValues.length > 0
    ) {
      //Read the domain values of the subproperty and create a grid row for each
      displayUnitSubProp.propertyValues.forEach((pv) => {
        let value = pv?.valueAttributeCells.find((vac) => {
          if (vac.propertyValueAttribute.name === "Value") return vac;
        })?.value;
        if (!value) value = "";

        //Look for the RoundMethod pva and validate the value
        const roundMethod = pv?.valueAttributeCells.find((vac) => {
          if (vac.propertyValueAttribute.name === StandardPVAs.RoundMethod)
            return vac.value;
        })?.value;
        const validatedRoundMethod: RoundMethod =
          ValidateRoundMethod(roundMethod);

        //Look for the RoundDigits pva and validate the value
        const roundDigits = pv?.valueAttributeCells.find((vac) => {
          if (vac.propertyValueAttribute.name === StandardPVAs.RoundDigits)
            return vac.value;
        })?.value;
        const validatedRoundDigits = ValidateRoundDigits(roundDigits);

        //Create a new row for the grid
        const row: DisplayUnitsDataGridRow = CreateDisplayUnitsDataGridRow(
          value,
          false,
          validatedRoundMethod,
          validatedRoundDigits
        );
        displayUnitRows.push(row);
      });
    }

    return displayUnitRows;
  };

  const getDefaultUnit = (): string => {
    //Determine what our default value should be
    // 1) If there is a .DISPLAYUNIT.DEFAULT subproperty
    // 2) If .DISPLAYUNIT.DEFAULT subproperty has at least one, non-null value, use first.
    // 3) Failing the above, if there is a non-null primary unit, use that.
    let defaultValue = "";

    //See if the .DISPLAYUNIT.DEFAULT subproperty exists
    const defaultSubProp = findSubProperty(
      StandardSubproperties.DISPLAYUNIT_DEFAULT
    );
    if (defaultSubProp) {
      //Read the domain values of the subproperty and get the first one.
      const values: string[] | undefined = GetPropertyValues(
        defaultSubProp
      )?.filter((v) => v !== null);

      // See if we have at least one value, if so use that as our default value.
      if (values && values.length > 0) {
        defaultValue = values[0];
      }
    }

    return defaultValue;
  };

  const findSubProperty = (subpropName: string): PropertyResponse | null => {
    //Build the subprop name
    const fullSubPropName =
      parentPropertyContext.parentPropertyName + subpropName;

    //Find all the detail on the parent property's subproperties
    const mySubproperties =
      subproperties.byParentPropertyId[parentPropertyContext.parentId];
    if (!mySubproperties) return null;

    let targetSubProperty = null;
    for (const key in mySubproperties.standardById) {
      const subProperty = mySubproperties.standardById[key];
      if (subProperty.name === fullSubPropName) {
        targetSubProperty = subProperty;
        break;
      }
    }

    return targetSubProperty;
  };

  // Create the .UNIT Property object
  const createUnitSubproperty = (): Property => {
    return BuildSubproperty({
      parentPropertyContext,
      subPropertyName: StandardSubproperties.UNIT, // e.g. "_UNIT"
      attributeNames: [StandardPVAs.Value],
      propertyValueAttributes,
      comments: "Generated via Create Units Macro",
      getPropValues: (attrs) => [
        {
          valueAttributeCells: [
            {
              propertyValueAttributeId: attrs[StandardPVAs.Value].id,
              value: primaryUnit?.label ?? "",
            },
          ],
        },
      ],
    });
  };

  // Create the .DISPLAYUNIT Property object
  const createDisplayUnitSubproperty = (): Property => {
    return BuildSubproperty({
      parentPropertyContext,
      subPropertyName: StandardSubproperties.DISPLAYUNIT,
      attributeNames: [
        StandardPVAs.Value,
        StandardPVAs.RoundMethod,
        StandardPVAs.RoundDigits,
      ],
      propertyValueAttributes,
      comments: "Generated via Create Units Macro",
      getPropValues: (attrs) =>
        rows.map((row) => ({
          valueAttributeCells: [
            {
              propertyValueAttributeId: attrs[StandardPVAs.Value].id,
              value: row.displayUnit,
            },
            {
              propertyValueAttributeId: attrs[StandardPVAs.RoundMethod].id,
              value: row.roundMethod.toString(),
            },
            {
              propertyValueAttributeId: attrs[StandardPVAs.RoundDigits].id,
              value: row.roundDigit !== null ? row.roundDigit.toString() : "",
            },
          ],
        })),
    });
  };

  // Create the .DISPLAYUNIT.DEFAULT Property object
  const createDisplayUnitDefaultSubproperty = (): Property | null => {
    if (!parentPropertyContext) {
      throw new Error(
        "parentPropertyContext is required but was not provided."
      );
    }
    const defaultRow = rows.find((row) => row.isDefault);
    if (!defaultRow) return null;

    return BuildSubproperty({
      parentPropertyContext,
      subPropertyName: StandardSubproperties.DISPLAYUNIT_DEFAULT,
      attributeNames: [StandardPVAs.Value],
      propertyValueAttributes,
      comments: "Generated via Create Default Units Macro",
      getPropValues: (attrs) => [
        {
          valueAttributeCells: [
            {
              propertyValueAttributeId: attrs[StandardPVAs.Value].id,
              value: defaultRow.displayUnit,
            },
          ],
        },
      ],
    });
  };

  const createSubproperties = () => {
    //Close the dialog
    setOpen(false);

    // Create the .UNIT, .DISPLAYUNIT, and .DISPLAYUNIT.DEFAULT database Property
    // objects we need to send to the API from the state of the component
    const newUnitSubProperty = createUnitSubproperty();
    const newDisplayUnitSubProperty = createDisplayUnitSubproperty();
    const newDisplayUnitDefaultSubProperty =
      createDisplayUnitDefaultSubproperty();

    //Add the new .UNIT subproperty
    dispatch(
      createOrReplaceStandardSubprop({
        property: newUnitSubProperty,
      })
    );

    //Add the new .DISPLAYUNIT subproperty
    dispatch(
      createOrReplaceStandardSubprop({
        property: newDisplayUnitSubProperty,
      })
    );

    //Add the new .DISPLAYUNIT subproperty if we have one
    if (newDisplayUnitDefaultSubProperty) {
      dispatch(
        createOrReplaceStandardSubprop({
          property: newDisplayUnitDefaultSubProperty,
        })
      );
    } else {
      dispatch(
        removeStandardSubPropertyPL({
          standardSubPropertyName:
            parentPropertyContext.parentPropertyName +
            StandardSubproperties.DISPLAYUNIT_DEFAULT,
          parentId: parentPropertyContext.parentId,
        })
      );
    }
  };

  //Build the content section for the dialog
  const buildContent = (
    <div>
      <Tooltip
        title="The selected Primary Unit will become the value of the .UNIT subproperty. This is the unit of measure the rules will use for the value of the RANGE property and any .MIN and .MAX subproperty values."
        placement="right"
        arrow
      >
        <Autocomplete
          id="primaryUnitDropdown"
          value={primaryUnit}
          onChange={(event: any, newValue: UnitInfo | null) => {
            setPrimaryUnit(newValue);
          }}
          autoHighlight
          blurOnSelect
          options={allUnitsRef.current}
          groupBy={(option) => option.group}
          getOptionLabel={(option) => option.label}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Primary Unit"
              size="small"
              error={Boolean(!primaryUnit)}
              helperText={primaryUnit ? "" : "Please supply a Primary Unit"}
            />
          )}
          sx={{ width: 300, marginTop: 1, marginBottom: 2 }}
        />
      </Tooltip>
      <DisplayUnitsDataGrid
        rows={rows}
        setRows={setRows}
        displayUnitOptions={activeGroupUnits}
        setIsGridInEditMode={setIsGridInEditMode}
      />
      {showWarning && (
        <Alert variant="filled" severity="warning">
          Clicking the &quot;Create Unit Subproperties&quot; button will replace
          any existing .UNIT, .DISPLAYUNIT and .DISPLAYUNIT.DEFAULT
          subproperties.
        </Alert>
      )}
    </div>
  );

  return (
    <ModalConfirmDialog
      open={open}
      isWarning={false}
      title={`Configure Unit Subproperties Macro`}
      content={buildContent}
      defaultButton="OK"
      cancelButtonText="Cancel"
      okButtonText="Create UNIT Subproperties"
      onCancelButtonClick={() => setOpen(false)}
      onOKButtonClick={() => createSubproperties()}
      okButtonDisabled={isGridInEditMode}
    />
  );
};

export default ModalConfirmDialog;
